import { UserActionTypes, UserAction, UserState } from './user.types';
import { STORAGE_KEYS } from '../../constants';

// Initial state
const initialState: UserState = {
  // User data
  currentUser: null,
  workspace: null,

  // Authentication state
  isAuthenticated: false,
  authToken: null, // Don't initialize from localStorage here to avoid hydration issues

  // Loading states
  loading: false,
  loginLoading: false,
  registerLoading: false,
  profileLoading: false,
  updateLoading: false,
  userDataLoading: false,
  workspaceDataLoading: false,

  // Error states
  error: null,
  loginError: null,
  registerError: null,
  profileError: null,
  updateError: null,
  userDataError: null,
  workspaceDataError: null,

  // Success states
  success: null,
  loginSuccess: false,
  registerSuccess: false,
  updateSuccess: false,
  userDataSuccess: false,
  workspaceDataSuccess: false,
};

// User reducer
const userReducer = (
  state: UserState = initialState,
  action: UserAction
): UserState => {
  switch (action.type) {
    // Initialization actions
    case UserActionTypes.INITIALIZE_AUTH: {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const userData = localStorage.getItem(STORAGE_KEYS.USER);
      const workspaceData = localStorage.getItem(STORAGE_KEYS.WORKSPACE);
      const parsedUser = userData ? JSON.parse(userData) : null;
      const parsedWorkspace = workspaceData ? JSON.parse(workspaceData) : null;
      return {
        ...state,
        authToken: token,
        isAuthenticated: !!token,
        currentUser: parsedUser ?? state.currentUser,
        workspace: parsedWorkspace ?? state.workspace,
      };
    }

    // Login actions
    case UserActionTypes.LOGIN_REQUEST:
      return {
        ...state,
        loginLoading: true,
        loginError: null,
        loginSuccess: false,
      };

    case UserActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        loginLoading: false,
        loginError: null,
        loginSuccess: true,
        currentUser: action.payload,
        isAuthenticated: true,
        authToken: localStorage.getItem(STORAGE_KEYS.TOKEN),
      };

    case UserActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        loginLoading: false,
        loginError: action.payload,
        loginSuccess: false,
        currentUser: null,
        isAuthenticated: false,
        authToken: null,
      };

    // Logout actions
    case UserActionTypes.LOGOUT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case UserActionTypes.LOGOUT_SUCCESS:
      return {
        ...initialState,
        authToken: null,
      };

    case UserActionTypes.LOGOUT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Register actions
    case UserActionTypes.REGISTER_REQUEST:
      return {
        ...state,
        registerLoading: true,
        registerError: null,
        registerSuccess: false,
      };

    case UserActionTypes.REGISTER_SUCCESS:
      return {
        ...state,
        registerLoading: false,
        registerError: null,
        registerSuccess: true,
        currentUser: action.payload,
        isAuthenticated: true,
        authToken: localStorage.getItem(STORAGE_KEYS.TOKEN),
      };

    case UserActionTypes.REGISTER_FAILURE:
      return {
        ...state,
        registerLoading: false,
        registerError: action.payload,
        registerSuccess: false,
      };

    // Get user profile actions
    case UserActionTypes.GET_USER_PROFILE_REQUEST:
      return {
        ...state,
        profileLoading: true,
        profileError: null,
      };

    case UserActionTypes.GET_USER_PROFILE_SUCCESS:
      return {
        ...state,
        profileLoading: false,
        profileError: null,
      };

    case UserActionTypes.GET_USER_PROFILE_FAILURE:
      return {
        ...state,
        profileLoading: false,
        profileError: action.payload,
      };

    // Update user profile actions
    case UserActionTypes.UPDATE_USER_PROFILE_REQUEST:
      return {
        ...state,
        updateLoading: true,
        updateError: null,
        updateSuccess: false,
      };

    case UserActionTypes.UPDATE_USER_PROFILE_SUCCESS:
      return {
        ...state,
        updateLoading: false,
        updateError: null,
        updateSuccess: true,
        currentUser: action.payload,
      };

    case UserActionTypes.UPDATE_USER_PROFILE_FAILURE:
      return {
        ...state,
        updateLoading: false,
        updateError: action.payload,
        updateSuccess: false,
      };

    // Get user data actions
    case UserActionTypes.GET_USER_DATA_REQUEST:
      return {
        ...state,
        userDataLoading: true,
        userDataError: null,
        userDataSuccess: false,
      };

    case UserActionTypes.GET_USER_DATA_SUCCESS:
      return {
        ...state,
        userDataLoading: false,
        userDataError: null,
        userDataSuccess: true,
        currentUser: action.payload,
        isAuthenticated: true,
        authToken: localStorage.getItem(STORAGE_KEYS.TOKEN) || state.authToken,
      };

    case UserActionTypes.GET_USER_DATA_FAILURE:
      return {
        ...state,
        userDataLoading: false,
        userDataError: action.payload,
        userDataSuccess: false,
        // Don't clear authentication on user data failure unless it's 401
        isAuthenticated: action.payload?.includes('401')
          ? false
          : state.isAuthenticated,
      };

    // Get workspace data actions
    case UserActionTypes.GET_WORKSPACE_DATA_REQUEST:
      return {
        ...state,
        workspaceDataLoading: true,
        workspaceDataError: null,
        workspaceDataSuccess: false,
      };

    case UserActionTypes.GET_WORKSPACE_DATA_SUCCESS:
      return {
        ...state,
        workspaceDataLoading: false,
        workspaceDataError: null,
        workspaceDataSuccess: true,
        workspace: action.payload,
      };

    case UserActionTypes.GET_WORKSPACE_DATA_FAILURE:
      return {
        ...state,
        workspaceDataLoading: false,
        workspaceDataError: action.payload,
        workspaceDataSuccess: false,
      };

    // Clear error actions
    case UserActionTypes.CLEAR_USER_ERROR:
      return {
        ...state,
        error: null,
        loginError: null,
        registerError: null,
        profileError: null,
        updateError: null,
        userDataError: null,
        workspaceDataError: null,
      };

    // Clear success actions
    case UserActionTypes.CLEAR_USER_SUCCESS:
      return {
        ...state,
        success: null,
        loginSuccess: false,
        registerSuccess: false,
        updateSuccess: false,
        userDataSuccess: false,
        workspaceDataSuccess: false,
      };

    // Set user data
    case UserActionTypes.SET_USER_DATA:
      return {
        ...state,
        currentUser: action.payload,
        isAuthenticated: true,
      };

    // Clear user data
    case UserActionTypes.CLEAR_USER_DATA:
      return {
        ...initialState,
        authToken: null,
      };

    default:
      return state;
  }
};

export default userReducer;
